import { useRef, useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription,
} from "../../../components/ui/alert-dialog";
import { FileArrowUp, X, ArrowClockwise } from "@phosphor-icons/react";
import {
  acceptExtForKind, validateSelectedFile, formatFileSize, formatLegalDate,
  isRetryableFailure, isValidationFailure, newIdempotencyKey, publishLegalVersion,
} from "../../../lib/legalAdmin";

const GENERIC_FAIL = "Publication could not be completed. Please try again.";

// One legal category card: select → confirm → publish, with correct idempotency-key lifecycle.
export default function CategoryCard({ category, onRefresh }) {
  const { category_key: key, title, kind } = category;
  const isPolicy = kind === "PUBLIC_POLICY";
  const [current, setCurrent] = useState(category.current || null);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [publishError, setPublishError] = useState(null); // { message, retryable }
  const [refreshFailed, setRefreshFailed] = useState(false);
  const uuidRef = useRef(null); // retained idempotency key for the current attempt
  const inputRef = useRef(null);

  const discardAttempt = () => { uuidRef.current = null; };

  const onSelect = (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = ""; // allow reselecting the same filename
    setPublishError(null);
    setRefreshFailed(false);
    if (!f) return;
    const { ok, error } = validateSelectedFile(f, kind);
    if (!ok) { setFile(null); setFileError(error); discardAttempt(); return; }
    setFile(f); setFileError(null); discardAttempt(); // new file → new attempt
  };

  const removeFile = () => {
    setFile(null); setFileError(null); setPublishError(null); discardAttempt();
  };

  const doPublish = async () => {
    if (!file) return;
    if (!uuidRef.current) uuidRef.current = newIdempotencyKey(); // created immediately before POST
    setSubmitting(true);
    setPublishError(null);
    try {
      const published = await publishLegalVersion(key, file, uuidRef.current);
      setCurrent(published);          // authoritative current from response
      discardAttempt();               // confirmed 200 → discard UUID
      setFile(null);
      setConfirmOpen(false);
      toast.success("Published successfully.");
      try {
        await onRefresh();            // refresh categories for authoritative state
      } catch (_) {
        setRefreshFailed(true);       // do NOT re-publish; keep the successful response
      }
    } catch (err) {
      if (isValidationFailure(err)) {
        discardAttempt();             // corrected file must use a new UUID
        setConfirmOpen(false);
        const msg = err?.response?.data?.detail?.message || "The uploaded file failed validation.";
        setPublishError({ message: msg, retryable: false });
      } else if (isRetryableFailure(err)) {
        // retain uuidRef.current → manual Try Again reuses the same key
        setPublishError({ message: GENERIC_FAIL, retryable: true });
      } else {
        discardAttempt();             // nonretryable → no same-attempt Try Again
        setConfirmOpen(false);
        setPublishError({ message: GENERIC_FAIL, retryable: false });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid-card p-5" data-testid={`legal-card-${key}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="heading text-base tracking-tight" data-testid={`legal-title-${key}`}>{title}</div>
          <div className="mt-1">
            {current ? (
              <span className="text-sm" data-testid={`legal-status-${key}`}>
                Version {current.system_version}
                <span className="text-[hsl(var(--muted))]">
                  {" · "}Published {formatLegalDate(current.published_at)}
                </span>
              </span>
            ) : (
              <span className="text-sm text-[hsl(var(--muted))]" data-testid={`legal-status-${key}`}>
                Not published
              </span>
            )}
          </div>
          {current && (
            <div className="text-xs text-[hsl(var(--muted))] mt-0.5" data-testid={`legal-current-file-${key}`}>
              {current.original_filename} · {formatFileSize(current.file_size_bytes)}
            </div>
          )}
        </div>
        <span className="id-chip shrink-0" data-testid={`legal-badge-${key}`}>{isPolicy ? "PDF" : "DOCX"}</span>
      </div>

      <div className="mt-4">
        {!file ? (
          <>
            <label className="btn-secondary inline-flex items-center gap-1.5 cursor-pointer text-sm" data-testid={`legal-upload-label-${key}`}>
              <FileArrowUp size={15} weight="bold" /> Select {isPolicy ? "PDF" : "DOCX"}
              <input
                ref={inputRef}
                type="file"
                accept={acceptExtForKind(kind)}
                className="hidden"
                onChange={onSelect}
                data-testid={`legal-file-input-${key}`}
                aria-label={`Select ${isPolicy ? "PDF" : "DOCX"} file to publish for ${title}`}
              />
            </label>
            {fileError && (
              <p className="text-xs text-[hsl(var(--danger))] mt-2" data-testid={`legal-file-error-${key}`}>{fileError}</p>
            )}
          </>
        ) : (
          <div className="space-y-2" data-testid={`legal-selected-${key}`}>
            <div className="flex items-center justify-between gap-3 border border-[hsl(var(--border))] rounded-[3px] px-3 py-2">
              <span className="text-sm truncate" data-testid={`legal-selected-name-${key}`}>
                {file.name} <span className="text-[hsl(var(--muted))]">· {formatFileSize(file.size)}</span>
              </span>
              <button type="button" onClick={removeFile} disabled={submitting}
                className="text-[hsl(var(--muted))] hover:text-[hsl(var(--danger))] disabled:opacity-40"
                data-testid={`legal-remove-${key}`} aria-label="Remove selected file">
                <X size={16} weight="bold" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              <label className="btn-ghost inline-flex items-center gap-1.5 cursor-pointer text-sm" data-testid={`legal-replace-label-${key}`}>
                Replace
                <input type="file" accept={acceptExtForKind(kind)} className="hidden"
                  onChange={onSelect} disabled={submitting}
                  aria-label={`Replace selected file for ${title}`} />
              </label>
              <button type="button" className="btn-primary inline-flex items-center gap-1.5 text-sm"
                onClick={() => { setPublishError(null); setConfirmOpen(true); }} disabled={submitting}
                data-testid={`legal-publish-${key}`}>
                Publish
              </button>
            </div>
          </div>
        )}

        {publishError && !confirmOpen && (
          <div className="mt-3" data-testid={`legal-publish-error-${key}`}>
            <p className="text-xs text-[hsl(var(--danger))]" role="alert">{publishError.message}</p>
            {publishError.retryable && (
              <button type="button" onClick={() => { setConfirmOpen(true); }}
                className="btn-secondary inline-flex items-center gap-1.5 text-sm mt-2"
                data-testid={`legal-try-again-${key}`}>
                <ArrowClockwise size={14} weight="bold" /> Try Again
              </button>
            )}
          </div>
        )}

        {refreshFailed && (
          <div className="mt-3 flex items-center gap-2" data-testid={`legal-refresh-failed-${key}`}>
            <p className="text-xs text-[hsl(var(--muted))]">Published, but the list didn’t refresh.</p>
            <button type="button" className="btn-ghost text-xs" onClick={() => onRefresh().catch(() => {})}
              data-testid={`legal-refresh-retry-${key}`}>Reload</button>
          </div>
        )}
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={(o) => { if (!submitting) setConfirmOpen(o); }}>
        <AlertDialogContent data-testid={`legal-confirm-${key}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Publish new version?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-1.5 text-sm">
                <div><span className="text-[hsl(var(--muted))]">Category:</span> {title}</div>
                {file && (
                  <div data-testid={`legal-confirm-file-${key}`}>
                    <span className="text-[hsl(var(--muted))]">File:</span> {file.name} · {formatFileSize(file.size)}
                  </div>
                )}
                <div data-testid={`legal-confirm-status-${key}`}>
                  <span className="text-[hsl(var(--muted))]">Status:</span>{" "}
                  {current ? `Current version: ${current.system_version}` : "Not published"}
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          {publishError && publishError.retryable && (
            <p className="text-xs text-[hsl(var(--danger))]" role="alert" data-testid={`legal-confirm-error-${key}`}>
              {publishError.message}
            </p>
          )}

          <AlertDialogFooter>
            <button type="button" className="btn-ghost text-sm" disabled={submitting}
              onClick={() => setConfirmOpen(false)} data-testid={`legal-cancel-${key}`}>
              Cancel
            </button>
            <button type="button" className="btn-primary text-sm" disabled={submitting}
              onClick={doPublish} data-testid={`legal-confirm-publish-${key}`} aria-live="polite">
              {submitting ? "Publishing…" : (publishError && publishError.retryable ? "Try Again" : "Publish Now")}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
